import { useQuery } from "convex/react";
import { useParams, useNavigate } from "react-router";
import { api } from "@convex/_generated/api";
import { Id } from "@convex/_generated/dataModel";
import { ShareView } from "@/components/share/share-view";
import { useState, useRef, useMemo, useEffect } from "react";
import { Screenshot } from "@capawesome/capacitor-screenshot";
import { Filesystem, Directory } from "@capacitor/filesystem";
import { Button } from "@/components/ui/button";
import { Share2, ArrowLeft } from "lucide-react";
import { useSettings } from "@/hooks/use-settings";
import { calculateFuelCost } from "@/lib/fuel-calculations";
import { useNativeTabs } from "@/lib/native-tabs-context";

export default function SharePage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [isSharing, setIsSharing] = useState(false);
  const shareButtonRef = useRef<HTMLDivElement>(null);
  const { hideTabsBar, showTabsBar } = useNativeTabs();

  // Hide native tabs bar on share page
  useEffect(() => {
    hideTabsBar();
    return () => {
      showTabsBar();
    };
  }, [hideTabsBar, showTabsBar]);

  const blockId = id as Id<"blocks"> | undefined;

  const data = useQuery(
    api.blocks.getBlockWithShift,
    blockId ? { id: blockId } : "skip"
  );
  const screenshotUrl = useQuery(
    api.files.getFileUrl,
    data?.routeScreenshotId ? { storageId: data.routeScreenshotId } : "skip"
  );
  const { settings } = useSettings();
  const latestFuelPrice = useQuery(api.expenses.getLatestFuelPrice);

  // Calculate fuel estimate (must be before conditional returns for hooks rules)
  const fuelEstimate = useMemo(() => {
    if (!data) return null;
    const shiftMiles = data.shift?.totalMiles;
    const miles = (shiftMiles && shiftMiles > 0) ? shiftMiles : (data.manualMiles ?? null);
    if (!miles || !settings?.mpg || !latestFuelPrice) return null;
    const cost = calculateFuelCost(miles, settings.mpg, latestFuelPrice);
    return {
      cost,
      mpg: settings.mpg,
      fuelPrice: latestFuelPrice,
    };
  }, [data, settings?.mpg, latestFuelPrice]);

  // Show loading state while checking params and authentication
  if (!blockId || data === undefined) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-900 to-slate-800 text-white flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-white mx-auto mb-4"></div>
          <p className="text-slate-400">Loading...</p>
        </div>
      </div>
    );
  }

  // If no data after loading, show not found
  if (!data) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-900 to-slate-800 text-white flex items-center justify-center">
        <div className="text-center">
          <p className="text-slate-400 mb-4">Block not found</p>
          <Button variant="outline" onClick={() => navigate("/blocks")}>
            Back to History
          </Button>
        </div>
      </div>
    );
  }

  const { shift, ...block } = data;

  const handleShare = async () => {
    try {
      console.log("[SharePage] Taking screenshot");

      // Hide share button before screenshot
      if (shareButtonRef.current) {
        shareButtonRef.current.style.visibility = 'hidden';
      }

      // Small delay to ensure hide is rendered
      await new Promise(resolve => setTimeout(resolve, 100));

      const result = await Screenshot.take();

      console.log("[SharePage] Screenshot taken, converting to blob");

      // Convert to blob
      let blob: Blob;

      if (result.uri.startsWith('data:')) {
        // Data URI (web)
        const base64Data = result.uri.split(',')[1];
        const byteCharacters = atob(base64Data);
        const byteNumbers = new Array(byteCharacters.length);

        for (let i = 0; i < byteCharacters.length; i++) {
          byteNumbers[i] = byteCharacters.charCodeAt(i);
        }

        const byteArray = new Uint8Array(byteNumbers);
        blob = new Blob([byteArray], { type: "image/png" });
      } else {
        // File path (native) - extract filename and read from correct directory
        const pathParts = result.uri.split('/');
        const filename = pathParts[pathParts.length - 1];
        const relativePath = `Caches/${filename}`;

        const fileData = await Filesystem.readFile({
          path: relativePath,
          directory: Directory.Library,
        });

        const base64 = typeof fileData.data === "string"
          ? fileData.data
          : await blobToBase64(fileData.data);

        const byteCharacters = atob(base64);
        const byteNumbers = new Array(byteCharacters.length);

        for (let i = 0; i < byteCharacters.length; i++) {
          byteNumbers[i] = byteCharacters.charCodeAt(i);
        }

        const byteArray = new Uint8Array(byteNumbers);
        blob = new Blob([byteArray], { type: "image/png" });
      }

      console.log(`[SharePage] Blob created: ${(blob.size / 1024 / 1024).toFixed(2)}MB`);

      const filename = `route-${block.date}.png`;
      const file = new File([blob], filename, { type: "image/png" });

      // Try Web Share API
      if (navigator.share && navigator.canShare?.({ files: [file] })) {
        console.log("[SharePage] Using Web Share API");
        try {
          await navigator.share({
            files: [file],
            title: "My Route",
            text: "Check out my route stats!",
          });
          console.log("[SharePage] Share successful");
          navigate(-1);
          return;
        } catch (error) {
          if ((error as Error).name !== "AbortError") {
            console.error("Share failed:", error);
          }
        }
      }

      // Fallback: Save to device
      console.log("[SharePage] Saving to device");
      const dir = "shared-images";
      const filePath = `${dir}/${filename}`;

      try {
        await Filesystem.mkdir({
          path: dir,
          directory: Directory.Documents,
          recursive: true,
        });
      } catch (mkdirError) {
        // Ignore "directory already exists" error
        if ((mkdirError as { code?: string }).code !== "OS-PLUG-FILE-0010") {
          throw mkdirError;
        }
      }

      const base64Data = await new Promise<string>((resolve, reject) => {
        const reader = new FileReader();
        reader.onloadend = () => resolve(reader.result as string);
        reader.onerror = reject;
        reader.readAsDataURL(blob);
      });

      const base64 = base64Data.split(",")[1];

      await Filesystem.writeFile({
        path: filePath,
        data: base64,
        directory: Directory.Documents,
      });

      console.log("[SharePage] Image saved to:", filePath);

      alert(`Image saved!\n\nYou can find it in:\nFiles > Documents > shared-images > ${filename}\n\nThen share it from the Files app.`);
      navigate(-1);
    } catch (error) {
      console.error("[SharePage] Failed to share:", error);
      alert("Failed to share. Please try again.");
    } finally {
      setIsSharing(false);
      // Show share button again
      if (shareButtonRef.current) {
        shareButtonRef.current.style.visibility = 'visible';
      }
    }
  };

  return (
    <div className="relative">
      <ShareView
        block={block}
        shift={shift}
        screenshotUrl={screenshotUrl}
        fuelEstimate={fuelEstimate}
      />

      {/* Compact Floating Share Button */}
      <div ref={shareButtonRef} className="fixed flex gap-2 bottom-6 left-1/2 -translate-x-1/2 z-50">
    <Button
          variant="ghost"
          onClick={() => navigate(-1)}
          className="bg-black/20 hover:bg-black/40 text-white px-6 py-3 rounded-full shadow-2xl font-semibold"
        ><ArrowLeft className="h-4 w-4 mr-2" />
         <span className="text-sm">Cancel</span>
        </Button>
        <Button
          onClick={handleShare}
          disabled={isSharing}
          className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-3 rounded-full shadow-2xl font-semibold"
        >
          <Share2 className="h-4 w-4 mr-2" />
          <span className="text-sm">{isSharing ? "Sharing..." : "Share"}</span>
        </Button>
      </div>
    </div>
  );
}

// Helper function
async function blobToBase64(blob: Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onloadend = () => {
      const result = reader.result as string;
      const base64Data = result.split(",")[1];
      resolve(base64Data);
    };
    reader.onerror = reject;
    reader.readAsDataURL(blob);
  });
}
